// format: [name, value]

import type { IExamTemplate, INoteType, INoteTypeComponent, IPatient } from '@/interfaces'

export const hospitalClinicOptions: [string, string][] = [
  ['Hospital Docs PA', 'Hospital Docs PA'],
  ['Medical Arts Hospital', 'Medical Arts Hospital'],
]

export const prefixOptions: [string, string][] = [
  ['Dr.', 'Dr.'],
  ['Mr.', 'Mr.'],
  ['Mrs.', 'Mrs.'],
  ['Ms.', 'Ms.'],
]

export const genderOptions: [string, Exclude<IPatient['gender'], undefined>][] = [
  ['Male', 'male'],
  ['Female', 'female'],
  ['Unspecified', 'unspecified'],
]

export const examTemplateTypeOptions: [string, IExamTemplate['type']][] = [
  ['ROS', 'ros'],
  ['Physical Exam', 'pe'],
]

export const visitTypeOptions: [string, INoteType['visit_type']][] = [
  ['Clinic', 'clinic'],
  ['First Visit', 'first visit'],
  ['Follow Up', 'follow up'],
  ['Inpatient', 'inpatient'],
  ['Office', 'office'],
]

export const specialityOptions: [string, INoteType['speciality']][] = [
  ['General', 'general'],
  ['Internal Medicine', 'internal medicine'],
  ['Functional Medicine', 'functional medicine'],
  ['Neurology', 'neurology'],
  ['Pain Management', 'pain management'],
  ['Rheumatology', 'rheumatology'],
  ['Orthopedics', 'orthopedics'],
]

export const shareOptions: [string, 'yes' | 'no'][] = [
  ['Yes', 'yes'],
  ['No', 'no'],
]

export const formatOptions: [string, INoteTypeComponent['format']][] = [
  ['List', 'list'],
  ['Narrative', 'narrative'],
  ['Problem Oriented', 'problem oriented'],
  ['Structured Template', 'structured template'],
]

export const formatOptionsWithExam: [string, INoteTypeComponent['format']][] = [
  ...formatOptions,
  ['Exam', 'exam'],
]

export const lengthOptions: [string, INoteTypeComponent['length']][] = [
  ['Concise', 'concise'],
  ['Normal', 'normal'],
  ['Verbose', 'verbose'],
]

export const NOTE_SECTIONS = {
  CC_HPI: 'CC/HPI',
  ROS: 'Review of Systems',
  PE: 'Physical Examination',
  AP: 'A/P',
  PATIENT_INSTRUCTIONS: 'Patient Instructions',
  DISCUSSION_NOTES: 'Discussion Notes',
}

export const frequencyOptions: string[] = [
  'every 2 weeks',
  'every 4 hours',
  'every 72 hours',
  '3 times a week',
  'every 3 hours',
  'every 8 hours',
  'every day',
  'every 24 hours',
  'every week',
  '5 times a day',
  'every 6-8 hours',
  '3 times a day',
  'as needed',
  'every 3 months',
  'every 6 hours',
  '4 times a day',
  'every 2 hours',
  'every 4 weeks',
  '2 times a day',
  'every 3 weeks',
  'every 3-4 hours',
  'every 12 hours',
  'every 2 day',
  'every 2 months',
  'every hour',
  '2 times a week',
  'every 4-6 hours',
  'every month',
  '6 times a day',
]

export const dosageQuantityUnitOptions: string[] = [
  'L',
  'application(s)',
  'applicator(s)ful',
  'becquerel(s)',
  'billion cells(s)',
  'caplet(s)',
  'capsule(s)',
  'cell(s)',
  'cm',
  'colony forming unit(s)',
  'cream(s)',
  'device(s)',
  'dose pk(s)',
  'drop(s)',
  'each',
  'equivalent islet number(s)',
  'film(s)',
  'g',
  'genome copy(s)',
  'gigabecquerel(s)',
  'gram of carbohydrate(s)',
  'implant(s)',
  'inch(es)',
  'inhalation(s)',
  'insert(s)',
  'irrigation(s)',
  'kg',
  'kilobecquerel(s)',
  'kilohertz(s)',
  'lesion(s)',
  'liquid(s)',
  'lollipop(s)',
  'lozenge(s)',
  'mCi',
  'mL',
  'megabecquerel(s)',
  'mg',
  'microcurie(s)',
  'microgram dietary folate equivalent(s)',
  'microgram(s)',
  'microliter(s)',
  'micromole(s)',
  'microunit(s)',
  'milliequivalent(s)',
  'milligram phenytoin equivalent(s)',
  'millimole(s)',
  'million cells',
  'million metric tons of coal equivalent(s)',
  'million plaque forming units(s)',
  'million units',
  'milliunit(s)',
  'nanogram(s)',
  'nebule(s)',
  'ophthalmic insert(s)',
  'ounce(s)',
  'package(s)',
  'packet(s)',
  'pad(s)',
  'patch(es)',
  'pellet(s)',
  'piece(s) of gum',
  'plaque forming unit(s)',
  'po pk(s)',
  'powder(s)',
  'puff(s)',
  'pump(s)',
  'recon(s)',
  'sachet(s)',
  'scoop(s)',
  'sliding scale dose(s)',
  'soln(s)',
  'solution(s)',
  'spray(s)',
  'startr pk(s)',
  'strip(s)',
  'suppositor(y/ies)',
  'suspension(s)',
  'syrup(s)',
  'tablet(s)',
  'tbsp',
  'thousand unit(s)',
  'towelette(s)',
  'tsp',
  'tube(s)',
  'unit(s)',
  'vaginal insert(s)',
  'vaginal ring(s)',
  'vector genome(s)',
  'vial(s)',
  'viral particle(s)',
  'wafer(s)',
  'x 10e11 vector genome(s)',
  'x 10e13 genome copy(s)',
  'x 10e14 vector genome(s)',
  'x 10e15 vector genome(s)',
  'x 10e6 car-positive viable t cells(s)',
  'x 10e6 cd34+ cells(s)',
  'x 10e8 car-positive viable t cells(s)',
  'x 10e8 plaque forming unit(s)',
  'x 10e9 mage-a4 tcr positive t cells(s)',
  'x 10e9 plaque forming unit(s)',
  'x 10e9 viable cells(s)',
  'x10e6 equivalent islet number(s)',
]

export const additionalInstructionsOptions: (string | null)[] = [
  null,
  'before meal(s)',
  'with meal(s)',
  'after meal(s)',
  'in the morning',
  'at noon',
  'in the evening',
  'at dinner',
  'at bedtime',
  'around the clock',
  'as directed',
  'as needed',
]
